import { Injectable } from '@angular/core';
import { ResourceService } from '../resource/resource.service';
import { HttpService } from '../http-service/http.service';
import { ServiceModule } from '../../../service.module';
import { map, Observable } from 'rxjs';
import {AvailableInstancesModel, AvailableInstanceTypesModel} from '../../../../core/models/resources/rtd.model';
import {ResourceModel} from "../../../../core/models/resources/resource.model";
import {RuleModel} from "../../../../core/models/misc/rule.model";

export interface MixinRef {
    group: string;
    type: string;
}

export interface TypeDetails {
    /** Always present (possibly []) when TypeDescription.details is set. */
    mixins: MixinRef[];
}

export interface TypeDescription {
    /** Whether the GVT is registered in the URM central catalog. */
    exists: boolean;
    /** Type metadata. Present iff `exists` is true. */
    details?: TypeDetails;
}

@Injectable({ providedIn: ServiceModule })
export class ResourceDataService extends ResourceService<any> {
    constructor(protected override httpService: HttpService<any>) {
        super(httpService);
    }

    getAvailableServices(path = ''): Observable<AvailableInstancesModel> {
        let url = `${path}/.schemas`;
        return super.getServices(url);
    }

    getAvailableInstanceTypes(path = ''): Observable<AvailableInstanceTypesModel> {
        let url = `${path}/.instances`;
        return super.getServices(url);
    }
    getPolicies(path = '', groupTypePairsList: Array<{ group: string; type: string }>): Observable<RuleModel[]> {
        let url = `${this.APIUrl}/${path}/.policies`;
        return this.httpService.queryPolicies(url, groupTypePairsList);
    }

    updateSubresources(resource: any): Observable<ResourceModel> {
        let url = `${this.APIUrl}/.subresources`;
        return this.httpService.update(url, resource).pipe(map((data) => this.enrichItem(data, false)));
    }

    describeType(group: string, version: string, type: string): Observable<TypeDescription> {
        const url = `${this.APIUrl}/resourceapis/${encodeURIComponent(group)}/${encodeURIComponent(version)}/${encodeURIComponent(type)}/.describe`;
        return this.httpService.get(url) as Observable<TypeDescription>;
    }

    getResourceUrl(): string {
        return 'resources';
    }

    getResourcesInPath(path: string, queryParam: string) {
        return queryParam ? super.getList(`${path}/.list?${queryParam}`) : super.getList(`${path}/.list`);
    }

    getFilteredResources(filters: any): Observable<ResourceModel[]> {
        const queryParams = new URLSearchParams();
        Object.keys(filters).forEach(key => {
            if (filters[key] !== undefined && filters[key] !== null) {
                queryParams.append(key, filters[key]);
            }
        });
        const url = `${this.APIUrl}/filteredresources/.list?${queryParams.toString()}`;
        return this.httpService.get(url).pipe(map((data: any) => {
            // The API returns a wrapper object with an items array
            // Each item is a FilteredResource with a nested resource object
            if (data && Array.isArray(data.items)) {
                return data.items.map((filteredResource: any) => {
                    // Extract the nested resource (K8s-style resource object)
                    const k8sResource = filteredResource.resource;

                    // Flatten the structure to match the expected ResourceModel format
                    // The regular list API returns resources with top-level fields extracted
                    const flattenedResource: any = {
                        apiVersion: k8sResource.apiVersion,
                        uid: k8sResource.metadata?.uid || '',
                        name: k8sResource.metadata?.name || '',
                        displayName: k8sResource.metadata?.displayName || k8sResource.spec?.displayName || '',
                        description: k8sResource.spec?.description || '',
                        type: k8sResource.type,
                        typeDisplayName: k8sResource.type, // Will be enriched later
                        path: k8sResource.metadata?.path || '',
                        creationTime: k8sResource.metadata?.creationTime || '',
                        modificationTime: k8sResource.metadata?.modificationTime || '',
                        labels: k8sResource.metadata?.labels || null,
                        annotations: k8sResource.metadata?.annotations || null,
                        conditions: k8sResource.status?.conditions || [],
                        resource: k8sResource, // Keep the full nested resource object
                        customSubresources: null
                    };

                    return this.enrichItem(flattenedResource, true);
                });
            }
            return [];
        }));
    }

    getDiscoveryApiResources(path: string): Observable<string[]> {
        const url = `${this.APIUrl}/discoveryapi/.list?path=${encodeURIComponent(path)}`;
        return this.httpService.get(url).pipe(map((data: any) => {
            const items = data.items || [];
            // Sort by Name first, then by GVT.type as fallback
            return items.sort((a: any, b: any) => {
                const nameA = (a.Name || '').toLowerCase();
                const nameB = (b.Name || '').toLowerCase();

                // If both have names and they're different, sort by name
                if (nameA && nameB) {
                    if (nameA !== nameB) {
                        return nameA.localeCompare(nameB);
                    }
                    // If names are equal, fall through to sort by type
                }

                // If only one has a name, prioritize the one with a name
                if (nameA && !nameB) {
                    return 1; // a comes after b
                }
                if (!nameA && nameB) {
                    return -1; // a comes before b
                }

                // Both names are empty or equal, sort by GVT.type
                const typeA = (a.GVT?.type || '').toLowerCase();
                const typeB = (b.GVT?.type || '').toLowerCase();
                return typeA.localeCompare(typeB);
            });
        }));
    }
}
